import axiosInstance from '../utils/AxiosConfig';

// El interceptor global redirige a /permission-denied en cualquier 403 y muestra un mensaje genérico en los 400.
// Acá la pantalla decide: se desactivan ambos y los errores se traducen con describeTablesError.
const REQUEST_OPTIONS = { skipGlobalErrorRedirect: true, skipSnackbar: true };

const unwrap = (response) => response.data.data;

class TablesService {
  // ---- Plano ----
  static async getAreas(locationId) {
    return unwrap(await axiosInstance.get('/tables/areas', { params: { locationId }, ...REQUEST_OPTIONS }));
  }

  static async createArea({ locationId, name }) {
    return unwrap(await axiosInstance.post('/tables/areas', { location_id: locationId, name }, REQUEST_OPTIONS));
  }

  static async updateArea(areaId, { name, sortOrder }) {
    return unwrap(await axiosInstance.put(`/tables/areas/${areaId}`, { name, sort_order: sortOrder }, REQUEST_OPTIONS));
  }

  static async deleteArea(areaId) {
    return unwrap(await axiosInstance.delete(`/tables/areas/${areaId}`, REQUEST_OPTIONS));
  }

  static async createTable({ areaId, name, capacity, shape, posX, posY, width, height }) {
    return unwrap(
      await axiosInstance.post(
        '/tables/dining',
        { area_id: areaId, name, capacity, shape, pos_x: posX, pos_y: posY, width, height },
        REQUEST_OPTIONS
      )
    );
  }

  /** Solo datos de la mesa; la posición y el tamaño se guardan con saveLayout. */
  static async updateTable(tableId, { name, capacity, shape }) {
    return unwrap(await axiosInstance.put(`/tables/dining/${tableId}`, { name, capacity, shape }, REQUEST_OPTIONS));
  }

  static async deleteTable(tableId) {
    return unwrap(await axiosInstance.delete(`/tables/dining/${tableId}`, REQUEST_OPTIONS));
  }

  static async saveLayout(areaId, payload) {
    return unwrap(await axiosInstance.put(`/tables/areas/${areaId}/layout`, payload, REQUEST_OPTIONS));
  }

  /** Caja abierta en la terminal. Un 400 (terminal que no es de esta sucursal) lo interpreta la pantalla, sin toast global. */
  static async isCashOpen(locationId, terminalCode) {
    return unwrap(await axiosInstance.get('/cash_register/is_open', { params: { locationId, terminalCode }, ...REQUEST_OPTIONS }));
  }

  /** Personal asignado a la sucursal: los mozos que se pueden elegir. */
  static async getWaiters(locationId) {
    return unwrap(await axiosInstance.get('/tables/waiters', { params: { locationId }, ...REQUEST_OPTIONS }));
  }

  // ---- Servicio ----
  static async getFloor(locationId) {
    return unwrap(await axiosInstance.get('/tables/floor', { params: { locationId }, ...REQUEST_OPTIONS }));
  }

  static async openTab(tableId, { guests, waiterEmail } = {}) {
    return unwrap(
      await axiosInstance.post(`/tables/dining/${tableId}/tab`, { guests, waiter_email: waiterEmail }, REQUEST_OPTIONS)
    );
  }

  static async getTab(tabId) {
    return unwrap(await axiosInstance.get(`/tables/tabs/${tabId}`, REQUEST_OPTIONS));
  }

  static async updateTab(tabId, { guests, waiterEmail }) {
    return unwrap(
      await axiosInstance.put(
        `/tables/tabs/${tabId}`,
        { guests, waiter_email: waiterEmail },
        REQUEST_OPTIONS
      )
    );
  }

  /** items: [{ productId, quantity, notes }] */
  static async addItems(tabId, items) {
    const body = { items: items.map((item) => ({ product_id: item.productId, quantity: item.quantity, notes: item.notes })) };
    return unwrap(await axiosInstance.post(`/tables/tabs/${tabId}/items`, body, REQUEST_OPTIONS));
  }

  static async voidItem(tabId, itemId, reason) {
    return unwrap(await axiosInstance.post(`/tables/tabs/${tabId}/items/${itemId}/void`, { reason }, REQUEST_OPTIONS));
  }

  static async closeTab(tabId) {
    return unwrap(await axiosInstance.post(`/tables/tabs/${tabId}/close`, {}, REQUEST_OPTIONS));
  }

  static async moveTab(tabId, targetTableId) {
    return unwrap(
      await axiosInstance.post(`/tables/tabs/${tabId}/move`, { target_table_id: targetTableId }, REQUEST_OPTIONS)
    );
  }

  static async moveItems(tabId, itemIds, targetTableId) {
    return unwrap(
      await axiosInstance.post(
        `/tables/tabs/${tabId}/move-items`,
        { item_ids: itemIds, target_table_id: targetTableId },
        REQUEST_OPTIONS
      )
    );
  }

  static async linkTable(tabId, tableId) {
    return unwrap(await axiosInstance.post(`/tables/tabs/${tabId}/links`, { table_id: tableId }, REQUEST_OPTIONS));
  }

  static async unlinkTable(tabId, tableId) {
    return unwrap(await axiosInstance.delete(`/tables/tabs/${tabId}/links/${tableId}`, REQUEST_OPTIONS));
  }

  /**
   * Cobra la cuenta (o solo itemIds). `payment` lleva los mismos datos que una venta del POS:
   * { terminalCode, paymentMethod, paymentCategoryId, paymentSubcategoryId, paymentCurrency, client, company, adenda,
   *   paymentTransactionId }.
   */
  static async charge(tabId, { itemIds, ...payment }) {
    const body = {
      item_ids: itemIds,
      terminal_code: payment.terminalCode,
      payment_method: payment.paymentMethod,
      payment_category_id: payment.paymentCategoryId,
      payment_subcategory_id: payment.paymentSubcategoryId,
      payment_transaction_id: payment.paymentTransactionId,
      payment_currency: payment.paymentCurrency,
      payments: payment.payments,
      client: payment.client,
      company: payment.company,
      adenda: payment.adenda,
    };
    return unwrap(await axiosInstance.post(`/tables/tabs/${tabId}/charge`, body, REQUEST_OPTIONS));
  }
}

export default TablesService;
